import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';

import { ColumnComponentProps } from '../../../types/activity-column-types';
import {
  formatCurrency,
  getActivityItemDisplay,
  getCheckoutSubtotal,
  hasSubtotalValue,
  isOrderItemSubtotalEmpty,
  shouldShowFulfillmentSubtotalDiscrepancy,
} from '../../../utils/activity-formatters';

type FulfillmentSubtotalKey =
  | 'processed_subtotal'
  | 'delivered_subtotal'
  | 'received_subtotal';

type Props = ColumnComponentProps<
  BackofficeActivitiesOrderItem,
  number | null | undefined
> & {
  mode: 'checkout' | FulfillmentSubtotalKey;
};

export function StageSubtotalColumn({ info, mode }: Props) {
  const item = info.row.original;
  const checkoutSub = getCheckoutSubtotal(item);
  const display = getActivityItemDisplay(item);

  if (mode === 'checkout') {
    if (!display.showPriceSection) {
      return null;
    }

    if (isOrderItemSubtotalEmpty(item)) {
      return (
        <Text
          fontSize="sm"
          fontWeight="400"
          color="#373F41"
          textAlign="center"
          w="full"
        >
          -
        </Text>
      );
    }

    // checkout_subtotal is a nullable snapshot field; legacy orders predating it
    // fall back to the checkout-time subtotal derived from original_subtotal.
    // NOTE: readActivityDetail doesn't SELECT checkout_subtotal yet (see activity-formatters.ts
    // resolveOrderItemCheckoutPriceBase note), so this currently always resolves via the fallback.
    const checkoutSubtotalValue = item.checkout_subtotal ?? display.initialTotalPrice;

    return (
      <VStack align="center" spacing={0} w="full">
        <Text fontSize="sm" fontWeight="400" color="#373F41" textAlign="center">
          {formatCurrency(checkoutSubtotalValue)}
        </Text>
      </VStack>
    );
  }

  const groupSub = item[mode];

  if (!hasSubtotalValue(groupSub)) {
    return (
      <Text fontSize="sm" fontWeight="400" color="#373F41" textAlign="center">
        -
      </Text>
    );
  }

  const showDot = shouldShowFulfillmentSubtotalDiscrepancy(
    checkoutSub,
    groupSub
  );

  return (
    <HStack justify="center" spacing={1} w="full" flexWrap="nowrap">
      <Text fontSize="sm" fontWeight="400" color="#373F41">
        {formatCurrency(groupSub)}
      </Text>
      {showDot ? (
        <Box
          w="6px"
          h="6px"
          borderRadius="full"
          bg="red.500"
          flexShrink={0}
          aria-label="Subtotal berbeda dari checkout"
        />
      ) : null}
    </HStack>
  );
}
